/**
 * InstructorPatterns — read-only pupil pattern view for instructors.
 *
 * Calm, supportive, never overwhelming. Surfaces recurring issues
 * (dormant, stalled, repeated review failures) on the instructor's
 * OWN pupils only. No mutations, no scan trigger, no acknowledge —
 * those live on the admin surface.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp, Clock, ChevronLeft, AlertCircle, Info, AlertTriangle, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY = {
  critical: { label: "Critical", icon: AlertTriangle, cls: "bg-rose-100 text-rose-700 border-rose-300" },
  high:     { label: "High",     icon: AlertCircle,  cls: "bg-amber-100 text-amber-700 border-amber-300" },
  medium:   { label: "Medium",   icon: Info,         cls: "bg-blue-100 text-blue-700 border-blue-300" },
  low:      { label: "Low",      icon: Info,         cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

const PATTERN_TYPE_LABEL = {
  repeated_review_failures: "Repeated review failures",
  dormant_learner:          "Dormant pupil",
  stalled_progression:      "Stalled progression",
};

function relativeTime(iso) {
  if (!iso) return "—";
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function InstructorPatterns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/api/instructor/patterns/pupils`, { withCredentials: true })
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load pupil patterns."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="instructor-patterns" className="min-h-dvh bg-slate-50 dark:bg-slate-950 pb-12">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link
            to="/instructor/work"
            data-testid="instructor-patterns-back"
            className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </Link>
          <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="font-extrabold text-slate-900 dark:text-white text-base leading-tight">Pupil patterns</p>
            <p className="text-[11px] text-slate-500 leading-tight">Recurring signals · your pupils only · calm view</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-5 space-y-3">
        <div className="rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-4 text-[12.5px] text-indigo-900 dark:text-indigo-200">
          <p className="font-bold flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3.5 h-3.5" /> What this shows
          </p>
          <p className="leading-snug">
            Patterns are <strong>what KEEPS happening</strong>, not what's happening right now. We only surface recurring
            signals from your own pupils — quiet observations to help you act before they become urgent.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="instructor-patterns-empty" className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center">
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">No recurring patterns on your pupils.</p>
            <p className="text-xs text-slate-500 mt-1">Quiet is good. Carry on.</p>
          </div>
        ) : (
          <ul data-testid="instructor-patterns-list" className="space-y-2">
            {items.map((p) => {
              const sev = SEVERITY[p.severity] || SEVERITY.low;
              const SevIcon = sev.icon;
              const label = PATTERN_TYPE_LABEL[p.pattern_type] || p.pattern_type;
              return (
                <li
                  key={p.id}
                  data-testid={`instructor-patterns-row-${p.id}`}
                  className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
                >
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${sev.cls}`}>
                      <SevIcon className="w-2.5 h-2.5" />{sev.label}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{p.status}</span>
                    <span className="ml-auto text-xs text-slate-500 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {relativeTime(p.last_seen_at)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-bold text-slate-900 dark:text-white">
                    {label} — {p.affected?.label || "your pupil"}
                  </p>
                  <p className="mt-1 text-[12.5px] text-slate-600 dark:text-slate-300 leading-snug">{p.evidence_summary}</p>
                  {p.suggested_intervention && (
                    <p className="mt-2 text-[12.5px] text-indigo-700 dark:text-indigo-300 leading-snug">
                      <strong>Suggested:</strong> {p.suggested_intervention}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
